import AddIcon from '@mui/icons-material/Add'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { Box, Card, IconButton, Tooltip, Typography } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import EditIcon from '../images/EditIcon.png'
import { LanguageContext } from '../util/LanguageContext'
import { ModalDelDialog } from './ModalDelDialog'
import { ModalEditDialog } from './ModalEditDialog'

interface IllustrationCardProps {
  title: string
  id: string
  text: string
  file: string
  fullFile: string
  onEditFile: (text: string, file: File) => void
  onEditFullFile: (text: string, fullFile: File) => void
  onEdit: (oldText: string, newText: string) => void
  onDel: (text: string) => void
}

export const IllustrationCard: React.FC<IllustrationCardProps> = props => {
  const { title, id, text, file, fullFile, onEditFile, onEditFullFile, onEdit, onDel } = props
  const auth = useAuth()
  const fileUrl: string = file
  const fullFileUrl: string = fullFile
  const [isHovered, setIsHovered] = React.useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openDelDialog, setOpenDelDialog] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedFullFile, setSelectedFullFile] = useState<File | null>(null)
  const [isFullFile, setIsFullFile] = React.useState(false)
  const translation = useContext(LanguageContext)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const fullFileInputRef = useRef<HTMLInputElement>(null)
  const videoFileRef = useRef<HTMLVideoElement>(null)
  const videoFullFileRef = useRef<HTMLVideoElement>(null)
  const regexp = /(mov|.3gp|.mp4|.MP4|.ts|.webm|.mkv)$/

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setSelectedFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }
  const handleFullFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setSelectedFullFile(file)
    if (fullFileInputRef.current) fullFileInputRef.current.value = ''
  }

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  const handleEditClick = () => setOpenEditDialog(true)
  const handleDeleteClick = () => setOpenDelDialog(true)
  const handleClose = () => {
    setOpenEditDialog(false)
    setOpenDelDialog(false)
  }

  const handleEdit = (newText: string) => {
    onEdit(text, newText)
    handleClose()
  }
  const handleDel = () => {
    onDel(text)
    handleClose()
  }

  const handleFileMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.cursor = 'pointer'
  }
  const handleFileMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.cursor = 'default'
  }
  const handleFileClick = () => {
    fileInputRef.current?.click()
    setIsFullFile(false)
  }
  const handleFullFileClick = () => {
    fullFileInputRef.current?.click()
    setIsFullFile(true)
  }

  useEffect(() => {
    if (isFullFile) {
      selectedFullFile && onEditFullFile(text, selectedFullFile)
    } else {
      selectedFile && onEditFile(text, selectedFile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, selectedFullFile, videoFileRef, videoFullFileRef])

  return (
    <>
      {openEditDialog && (
        <ModalEditDialog
          open={openEditDialog}
          title={title}
          id={id}
          text={text}
          onClose={handleClose}
          onSave={handleEdit}
        />
      )}

      {openDelDialog && (
        <ModalDelDialog
          open={openDelDialog}
          title={title}
          id={id}
          text={text}
          onClose={handleClose}
          onDel={handleDel}
        />
      )}
      <Card sx={{ height: '288px', width: '461px', borderRadius: '10px' }}>
        <Box
          sx={{
            height: '57px',
            backgroundColor: isHovered ? '#F2F2F2' : '#282828',
            display: 'flex',
            alignItems: 'center',
            px: 2, // 左右のpaddingを追加
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Tooltip title={text} enterTouchDelay={0}>
            <Typography
              sx={{
                color: isHovered ? '#282828' : '#FFFFFF',
                fontSize: '21px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '340px',
              }}
            >
              {text}
            </Typography>
          </Tooltip>
          {isHovered && (
            <>
              <IconButton
                disabled={auth.isDisable}
                onClick={handleEditClick}
                sx={{
                  marginLeft: 'auto', // 右側に移動
                }}
              >
                <img src={EditIcon} width="25px" height="25px" alt="" />
              </IconButton>
              <IconButton
                disabled={auth.isDisable}
                onClick={handleDeleteClick}
                sx={{
                  color: 'red',
                  '& .MuiSvgIcon-root': {
                    fontSize: '40px',
                  },
                }}
              >
                <HighlightOffIcon />
              </IconButton>
            </>
          )}
        </Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '231px',
            width: '100%',
            backgroundColor: '#F2F2F2',
          }}
        >
          {fileUrl === 'null' ? (
            <Box
              sx={{
                border: '1px dashed #282828',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '106px',
                height: '183px',
                background: '#FFFFFF 0% 0% no-repeat padding-box',
              }}
              onClick={handleFileClick}
              onMouseEnter={handleFileMouseEnter}
              onMouseLeave={handleFileMouseLeave}
            >
              <input
                disabled={auth.isDisable}
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".jpg, .jpeg, .png, .mp4, .JPG, .JPEG, .PNG, .MP4"
                onChange={handleFileChange}
              />
              <AddIcon sx={{ width: '18px', height: '18px' }} />
            </Box>
          ) : (
            <Box
              onClick={handleFileClick}
              onMouseEnter={handleFileMouseEnter}
              onMouseLeave={handleFileMouseLeave}
            >
              {regexp.test(fileUrl.split('?')[0]) ? (
                <video
                  controls
                  src={fileUrl}
                  ref={videoFileRef}
                  style={{
                    width: '106px',
                    height: '183px',
                    objectFit: 'fill',
                  }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt={text}
                  style={{
                    width: '106px',
                    height: '183px',
                    objectFit: 'fill',
                  }}
                />
              )}
              <input
                disabled={auth.isDisable}
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".jpg, .jpeg, .png, .mp4, .JPG, .JPEG, .PNG, .MP4"
                onChange={handleFileChange}
              />
            </Box>
          )}
          {fullFileUrl === 'null' ? (
            <Box
              sx={{
                border: '1px dashed #282828',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                ml: '32px',
                width: '251px',
                height: '183px',
                background: '#FFFFFF 0% 0% no-repeat padding-box',
              }}
              onClick={handleFullFileClick}
              onMouseEnter={handleFileMouseEnter}
              onMouseLeave={handleFileMouseLeave}
            >
              <input
                disabled={auth.isDisable}
                type="file"
                ref={fullFileInputRef}
                style={{ display: 'none' }}
                accept=".jpg, .jpeg, .png, .mp4, .JPG, .JPEG, .PNG, .MP4"
                onChange={handleFullFileChange}
              />
              <AddIcon sx={{ width: '18px', height: '18px' }} />
              <Typography
                sx={{
                  font: 'normal normal normal 21px/32px BIZ UDGothic',
                  ml: '16px',
                }}
              >
                {translation['full_screen']}
              </Typography>
            </Box>
          ) : (
            <Box
              onClick={handleFullFileClick}
              onMouseEnter={handleFileMouseEnter}
              onMouseLeave={handleFileMouseLeave}
            >
              {regexp.test(fullFileUrl.split('?')[0]) ? (
                <video
                  controls
                  src={fullFileUrl}
                  ref={videoFullFileRef}
                  style={{
                    marginLeft: '32px',
                    width: '251px',
                    height: '183px',
                    objectFit: 'fill',
                  }}
                />
              ) : (
                <img
                  src={fullFileUrl}
                  alt={text}
                  style={{
                    marginLeft: '32px',
                    width: '251px',
                    height: '183px',
                    objectFit: 'fill',
                  }}
                />
              )}
              <input
                disabled={auth.isDisable}
                type="file"
                ref={fullFileInputRef}
                style={{ display: 'none' }}
                accept=".jpg, .jpeg, .png, .mp4, .JPG, .JPEG, .PNG, .MP4"
                onChange={handleFullFileChange}
              />
            </Box>
          )}
        </Box>
      </Card>
    </>
  )
}